import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, Image, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator, Switch, Pressable } from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { apiService } from '../services/api';
import { MetaInsightsChart, MetaChartPoint } from './MetaInsightsChart';

export interface FacebookInsightsModalProps {
  visible: boolean;
  pageId: string;
  onClose: () => void;
}

type ContentTab = 'all' | 'reels' | 'posts' | 'stories';
const CONTENT_TABS: { id: ContentTab; label: string; supported: boolean }[] = [
  { id: 'all', label: 'All views', supported: true },
  { id: 'reels', label: 'Reels', supported: false },
  { id: 'posts', label: 'Posts', supported: true },
  { id: 'stories', label: 'Stories', supported: false },
];

const RANGE_PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 28 days', days: 28 },
  { label: 'Last 90 days', days: 90 },
] as const;

const CONTENT_TYPE_META: Record<string, string> = {
  photo: 'Photo',
  video: 'Video',
  link: 'Link',
  none: 'Other',
};

type StatKey = 'views' | 'threeSecond' | 'oneMinute' | 'watchTime';

// Which Graph API metric backs the chart when a given stat card is active —
// clicking a card re-plots the chart to that metric, mirroring Meta's own
// dashboard. "views" is handled separately (see loadChartSeries) since it
// aggregates real per-post post_media_view rather than a single Page-level
// metric — page_views_total is a different metric entirely (Page profile
// views, not content views). There is no documented Page-level "1-minute
// views" aggregate (only a post-level 60s-exclude metric exists), so that
// card has no chart series of its own and reads 0 — matches Meta's own
// behavior for Pages without long-form video.
const CHART_METRIC_FOR_STAT: Partial<Record<StatKey, string>> = {
  threeSecond: 'page_video_views',
  watchTime: 'page_video_view_time',
};

interface ViewsSummary {
  current: number;
  previous: number;
  changePct: number | null;
}

interface RecentPostWithViews {
  postId: string;
  message?: string;
  mediaUrl?: string;
  mediaType: string;
  publishedAt?: string;
  views: number;
}

function formatCompactNumber(n: number): string {
  if (n < 1000) return n.toLocaleString();
  if (n < 1_000_000) return `${(n / 1000).toFixed(n % 1000 >= 100 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

function formatDateRangeLabel(since: Date, until: Date): string {
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${fmt(since)} - ${fmt(until)}`;
}

function shortDateLabel(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function timeAgoLabel(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Facebook Page Insights viewer — replicates Meta's own Page Insights
 * dashboard: tab row (All views/Reels/Posts/Stories), 4 clickable stat
 * cards that each drive what the chart plots, a date-range dropdown
 * (rendered as its own Modal — not an absolutely-positioned sibling view —
 * so it can never be visually overridden/clipped by the stats row beneath
 * it), the dual-line views/publishing chart, side-by-side breakdown cards,
 * and a recent-posts filmstrip using REAL per-post view counts
 * (post_media_view, the current metric — post_impressions is deprecated).
 *
 * Reels/Stories tabs are explicit "not applicable" states, not fabricated
 * data — DISLIX only publishes standard Page feed posts, and Meta has no
 * confirmed public Graph API aggregate for either content type at the Page
 * level (their own dashboard appears to use non-public internal
 * aggregation for those two tabs). */
export const FacebookInsightsModal: React.FC<FacebookInsightsModalProps> = ({ visible, pageId, onClose }) => {
  const [activeTab, setActiveTab] = useState<ContentTab>('all');
  const [activeStat, setActiveStat] = useState<StatKey>('views');
  const [rangeIndex, setRangeIndex] = useState(1); // default: Last 28 days
  const [showRangeMenu, setShowRangeMenu] = useState(false);
  const [showPublishingActivity, setShowPublishingActivity] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [summary, setSummary] = useState<ViewsSummary | null>(null);
  const [videoStats, setVideoStats] = useState({ threeSecondViews: 0, watchTimeMinutes: 0 });
  const [chartData, setChartData] = useState<MetaChartPoint[]>([]);
  const [contentTypes, setContentTypes] = useState<Array<{ mediaType: string; views: number }>>([]);
  const [followerSplit, setFollowerSplit] = useState<{ followers: number; nonFollowers: number } | null>(null);
  const [recentPosts, setRecentPosts] = useState<RecentPostWithViews[]>([]);
  const [postsByDay, setPostsByDay] = useState<Map<string, number>>(new Map());

  const tabMeta = CONTENT_TABS.find((t) => t.id === activeTab)!;

  const { since, until } = useMemo(() => {
    const days = RANGE_PRESETS[rangeIndex].days;
    const untilDate = new Date();
    const sinceDate = new Date(untilDate.getTime() - days * 24 * 60 * 60 * 1000);
    return { since: sinceDate, until: untilDate };
  }, [rangeIndex]);

  const loadChartSeries = useCallback(async (statKey: StatKey, sinceIso: string, untilIso: string, dayPostCounts: Map<string, number>) => {
    // "Views" uses a dedicated endpoint that sums real per-post
    // post_media_view by publish day — page_views_total (used for the other
    // stat cards' page-level video metrics) is Facebook's Page-profile-views
    // metric, a different number entirely from content views, confirmed by
    // comparing against Meta's own dashboard where post-level views alone
    // summed well past what page_views_total returned.
    if (statKey === 'views') {
      const res = await apiService.getFacebookViewsChartSeries(pageId, { since: sinceIso, until: untilIso });
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        const points: MetaChartPoint[] = res.data.map((d) => ({
          date: shortDateLabel(d.date),
          views: d.views,
          posts: dayPostCounts.get(d.date) || 0,
        }));
        setChartData(points);
      } else {
        setChartData([]);
      }
      return;
    }

    const metric = CHART_METRIC_FOR_STAT[statKey];
    if (!metric) {
      setChartData([]);
      return;
    }
    const res = await apiService.getFacebookPageInsights(pageId, { metrics: [metric], period: 'day', since: sinceIso, until: untilIso });
    if (res.statusCode === 1000 && Array.isArray(res.data) && res.data[0]) {
      const points: MetaChartPoint[] = (res.data[0].values || []).map((v: any) => {
        const dayKey = v.end_time ? new Date(v.end_time).toISOString().slice(0, 10) : '';
        return {
          date: v.end_time ? shortDateLabel(v.end_time) : '',
          views: typeof v.value === 'number' ? v.value : 0,
          posts: dayPostCounts.get(dayKey) || 0,
        };
      });
      setChartData(points);
    } else {
      setChartData([]);
    }
  }, [pageId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sinceIso = since.toISOString();
      const untilIso = until.toISOString();

      if (!tabMeta.supported) {
        // Reels/Stories: no real data source — show the empty state rather
        // than a fabricated chart/stats.
        setSummary(null);
        setVideoStats({ threeSecondViews: 0, watchTimeMinutes: 0 });
        setChartData([]);
        setContentTypes([]);
        setFollowerSplit(null);
        setRecentPosts([]);
        setLoading(false);
        return;
      }

      const [summaryRes, videoStatsRes, contentTypeRes, followerRes, postsRes] = await Promise.all([
        apiService.getFacebookPageViewsSummary(pageId, { since: sinceIso, until: untilIso }),
        apiService.getFacebookPageVideoStats(pageId, { since: sinceIso, until: untilIso }),
        apiService.getFacebookViewsByContentType(pageId, { since: sinceIso, until: untilIso }),
        apiService.getFacebookViewsByFollowerStatus(pageId, { since: sinceIso, until: untilIso }),
        apiService.getFacebookRecentPostsWithViews(pageId, 8),
      ]);

      setSummary(summaryRes.statusCode === 1000 ? summaryRes.data : null);
      setVideoStats(videoStatsRes.statusCode === 1000 && videoStatsRes.data ? videoStatsRes.data : { threeSecondViews: 0, watchTimeMinutes: 0 });
      setContentTypes(contentTypeRes.statusCode === 1000 && Array.isArray(contentTypeRes.data) ? contentTypeRes.data : []);
      setFollowerSplit(followerRes.statusCode === 1000 ? followerRes.data : null);

      const posts: RecentPostWithViews[] = postsRes.statusCode === 1000 && Array.isArray(postsRes.data) ? postsRes.data : [];
      setRecentPosts(posts);

      const dayPostCounts = new Map<string, number>();
      for (const p of posts) {
        if (!p.publishedAt) continue;
        const day = new Date(p.publishedAt).toISOString().slice(0, 10);
        dayPostCounts.set(day, (dayPostCounts.get(day) || 0) + 1);
      }
      setPostsByDay(dayPostCounts);

      await loadChartSeries(activeStat, sinceIso, untilIso, dayPostCounts);

      if (summaryRes.statusCode !== 1000 && videoStatsRes.statusCode !== 1000) {
        setError('Could not load insights for this page');
      }
    } catch (e: any) {
      setError(e?.message || 'Could not load insights for this page');
    } finally {
      setLoading(false);
    }
    // activeStat intentionally excluded — switching stats re-plots the chart
    // via the dedicated effect below without re-fetching everything else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, since, until, activeTab, loadChartSeries]);

  useEffect(() => {
    if (visible) load();
  }, [visible, load]);

  // Clicking a stat card re-plots the chart to that metric without
  // re-fetching the stat cards/breakdowns/filmstrip themselves.
  useEffect(() => {
    if (!visible || loading || !tabMeta.supported) return;
    loadChartSeries(activeStat, since.toISOString(), until.toISOString(), postsByDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStat]);

  const contentTypeTotal = contentTypes.reduce((sum, c) => sum + c.views, 0);
  const followerTotal = (followerSplit?.followers ?? 0) + (followerSplit?.nonFollowers ?? 0);
  const followerPct = followerTotal > 0 ? ((followerSplit?.followers ?? 0) / followerTotal) * 100 : 0;
  const nonFollowerPct = 100 - followerPct;
  const changePct = summary?.changePct;
  const changeIsPositive = (changePct ?? 0) >= 0;

  const statCards: Array<{ key: StatKey; value: string; label: string; hasChart: boolean }> = [
    { key: 'views', value: formatCompactNumber(summary?.current ?? 0), label: 'Views', hasChart: true },
    { key: 'threeSecond', value: formatCompactNumber(videoStats.threeSecondViews), label: '3-second views', hasChart: true },
    { key: 'oneMinute', value: '0', label: '1-minute views', hasChart: false },
    { key: 'watchTime', value: `${formatCompactNumber(videoStats.watchTimeMinutes)}m`, label: 'Watch time', hasChart: true },
  ];

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <View style={styles.topBar}>
            <View style={styles.tabRow}>
              {CONTENT_TABS.map((tab) => (
                <TouchableOpacity
                  key={tab.id}
                  style={[styles.tabButton, activeTab === tab.id && styles.tabButtonActive]}
                  onPress={() => setActiveTab(tab.id)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.tabButtonText, activeTab === tab.id && styles.tabButtonTextActive]}>{tab.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <TouchableOpacity style={styles.rangeButton} onPress={() => setShowRangeMenu(true)} activeOpacity={0.7}>
              <Text style={styles.rangeButtonText} numberOfLines={1}>{RANGE_PRESETS[rangeIndex].label}: {formatDateRangeLabel(since, until)}</Text>
              <Ionicons name="chevron-down" size={14} color="#B0B3B8" />
            </TouchableOpacity>

            <TouchableOpacity onPress={onClose} style={styles.closeButton} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={22} color="#E4E6EB" />
            </TouchableOpacity>
          </View>

          {/* Rendered as its own top-level Modal (not an absolutely
              positioned sibling of the stats row) so it always paints above
              everything else and can never be visually overridden by the
              content beneath it. */}
          <Modal visible={showRangeMenu} transparent animationType="fade" onRequestClose={() => setShowRangeMenu(false)}>
            <Pressable style={styles.rangeMenuOverlay} onPress={() => setShowRangeMenu(false)}>
              <View style={styles.rangeMenuCard}>
                {RANGE_PRESETS.map((preset, i) => (
                  <TouchableOpacity
                    key={preset.label}
                    style={styles.rangeMenuItem}
                    onPress={() => { setRangeIndex(i); setShowRangeMenu(false); }}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.rangeMenuItemText, i === rangeIndex && styles.rangeMenuItemTextActive]}>{preset.label}</Text>
                    {i === rangeIndex && <Ionicons name="checkmark" size={16} color="#4599FF" />}
                  </TouchableOpacity>
                ))}
              </View>
            </Pressable>
          </Modal>

          {loading ? (
            <View style={styles.loading}>
              <ActivityIndicator size="large" color="#4599FF" />
            </View>
          ) : !tabMeta.supported ? (
            <View style={styles.loading}>
              <Ionicons name="film-outline" size={28} color="#B0B3B8" />
              <Text style={styles.errorText}>DISLIX doesn't publish {tabMeta.label.toLowerCase()} yet — nothing to show here.</Text>
            </View>
          ) : error ? (
            <View style={styles.loading}>
              <Ionicons name="alert-circle-outline" size={24} color="#B0B3B8" />
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : (
            <ScrollView contentContainerStyle={styles.scrollContent}>
              <View style={styles.statsRow}>
                {statCards.map((card) => (
                  <TouchableOpacity
                    key={card.key}
                    style={[styles.statCard, activeStat === card.key && styles.statCardActive, !card.hasChart && styles.statCardDisabled]}
                    onPress={() => card.hasChart && setActiveStat(card.key)}
                    activeOpacity={card.hasChart ? 0.7 : 1}
                    disabled={!card.hasChart}
                  >
                    <View style={styles.statValueRow}>
                      <Text style={card.key === 'views' ? styles.statValue : styles.statValueMuted}>{card.value}</Text>
                      {card.key === 'views' && changePct != null && (
                        <View style={styles.statChangeRow}>
                          <Ionicons name={changeIsPositive ? 'arrow-up' : 'arrow-down'} size={12} color={changeIsPositive ? '#4ADE80' : '#F87171'} />
                          <Text style={[styles.statChangeText, { color: changeIsPositive ? '#4ADE80' : '#F87171' }]}>
                            {Math.abs(changePct).toLocaleString(undefined, { maximumFractionDigits: 0 })}%
                          </Text>
                        </View>
                      )}
                    </View>
                    <View style={styles.statLabelRow}>
                      <Text style={styles.statLabel}>{card.label}</Text>
                      <Ionicons name="information-circle-outline" size={13} color="#B0B3B8" />
                    </View>
                  </TouchableOpacity>
                ))}
              </View>

              <View style={styles.chartSection}>
                <MetaInsightsChart data={chartData} height={260} showPublishingActivity={showPublishingActivity} />
                <View style={styles.publishingToggleRow}>
                  <Text style={styles.publishingToggleLabel}>Publishing activity</Text>
                  <Switch
                    value={showPublishingActivity}
                    onValueChange={setShowPublishingActivity}
                    trackColor={{ false: '#3A3B3C', true: '#4599FF' }}
                    thumbColor="#FFFFFF"
                  />
                </View>
              </View>

              <View style={styles.breakdownRow}>
                <View style={styles.breakdownCard}>
                  <Text style={styles.breakdownTitle}>Views by content type</Text>
                  {contentTypeTotal === 0 ? (
                    <Text style={styles.emptyInline}>No published posts in this range</Text>
                  ) : (
                    <View style={styles.contentTypeList}>
                      {contentTypes
                        .filter((c) => c.views > 0)
                        .sort((a, b) => b.views - a.views)
                        .map((c) => {
                          const pct = contentTypeTotal > 0 ? (c.views / contentTypeTotal) * 100 : 0;
                          return (
                            <View key={c.mediaType} style={styles.contentTypeRow}>
                              <Text style={styles.contentTypeLabel}>{CONTENT_TYPE_META[c.mediaType] || 'Other'}</Text>
                              <View style={styles.barTrack}>
                                <View style={[styles.barFill, { width: `${pct}%` }]} />
                              </View>
                              <Text style={styles.contentTypePct}>{pct.toFixed(1)}%</Text>
                            </View>
                          );
                        })}
                    </View>
                  )}
                </View>

                <View style={styles.breakdownCard}>
                  <Text style={styles.breakdownTitle}>Views by followers vs. non-followers</Text>
                  {followerTotal === 0 ? (
                    <Text style={styles.emptyInline}>No media views in this range</Text>
                  ) : (
                    <View style={styles.donutRow}>
                      <View style={styles.donutRing} />
                      <View style={styles.donutLegend}>
                        <Text style={styles.donutPctPrimary}>{nonFollowerPct.toFixed(1)}%</Text>
                        <View style={styles.donutLegendRow}>
                          <View style={[styles.donutDot, { backgroundColor: '#4599FF' }]} />
                          <Text style={styles.donutLegendLabel}>Non-followers</Text>
                        </View>
                        <Text style={styles.donutPctSecondary}>{followerPct.toFixed(1)}%</Text>
                        <View style={styles.donutLegendRow}>
                          <View style={[styles.donutDot, { backgroundColor: '#FFFFFF' }]} />
                          <Text style={styles.donutLegendLabel}>Followers</Text>
                        </View>
                      </View>
                    </View>
                  )}
                </View>
              </View>

              <View style={styles.recentPostsSection}>
                <Text style={styles.recentPostsTitle}>Recent posts</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.recentPostsRow}>
                  {recentPosts.map((post) => (
                    <View key={post.postId} style={styles.recentPostTile}>
                      {post.mediaUrl ? (
                        <Image source={{ uri: post.mediaUrl }} style={styles.recentPostImage} />
                      ) : (
                        <View style={[styles.recentPostImage, styles.recentPostImageFallback]}>
                          <Ionicons name="document-text-outline" size={20} color="#B0B3B8" />
                        </View>
                      )}
                      <Text style={styles.recentPostDate} numberOfLines={1}>{post.publishedAt ? timeAgoLabel(post.publishedAt) : ''}</Text>
                      <Text style={styles.recentPostViews}>{formatCompactNumber(post.views)}</Text>
                      <Text style={styles.recentPostViewsLabel}>Views</Text>
                    </View>
                  ))}
                  {recentPosts.length === 0 && <Text style={styles.emptyInline}>No posts yet</Text>}
                </ScrollView>
              </View>
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'flex-end',
  },
  sheet: {
    maxHeight: '92%',
    backgroundColor: '#18191A',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 16,
    gap: 16,
  },
  scrollContent: {
    gap: 16,
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tabRow: {
    flex: 1,
    flexDirection: 'row',
    gap: 4,
  },
  tabButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  tabButtonActive: {
    backgroundColor: 'rgba(69,153,255,0.15)',
  },
  tabButtonText: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: '#B0B3B8',
  },
  tabButtonTextActive: {
    color: '#4599FF',
  },
  rangeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#242526',
    maxWidth: 180,
  },
  rangeButtonText: {
    fontSize: 12,
    fontWeight: '600' as const,
    color: '#E4E6EB',
    flexShrink: 1,
  },
  rangeMenuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'flex-end',
    paddingTop: 100,
    paddingRight: 16,
  },
  rangeMenuCard: {
    backgroundColor: '#242526',
    borderRadius: 10,
    overflow: 'hidden',
    minWidth: 180,
  },
  rangeMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  rangeMenuItemText: {
    fontSize: 14,
    color: '#B0B3B8',
  },
  rangeMenuItemTextActive: {
    color: '#4599FF',
    fontWeight: '600' as const,
  },
  closeButton: {
    padding: 4,
  },
  loading: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 48,
  },
  errorText: {
    fontSize: 14,
    color: '#B0B3B8',
    textAlign: 'center',
    paddingHorizontal: 24,
  },
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  statCard: {
    flexGrow: 1,
    flexBasis: '46%',
    backgroundColor: '#242526',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'transparent',
    padding: 14,
    gap: 8,
  },
  statCardActive: {
    borderColor: '#4599FF',
  },
  statCardDisabled: {
    opacity: 0.6,
  },
  statValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statValue: {
    fontSize: 26,
    fontWeight: '700' as const,
    color: '#E4E6EB',
  },
  statValueMuted: {
    fontSize: 18,
    fontWeight: '700' as const,
    color: '#E4E6EB',
  },
  statChangeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  statChangeText: {
    fontSize: 13,
    fontWeight: '700' as const,
  },
  statLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statLabel: {
    fontSize: 13,
    color: '#B0B3B8',
  },
  chartSection: {
    backgroundColor: '#242526',
    borderRadius: 10,
    padding: 16,
  },
  publishingToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
  },
  publishingToggleLabel: {
    fontSize: 13,
    color: '#E4E6EB',
  },
  breakdownRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  breakdownCard: {
    flexGrow: 1,
    flexBasis: '46%',
    backgroundColor: '#242526',
    borderRadius: 10,
    padding: 16,
    gap: 10,
  },
  breakdownTitle: {
    fontSize: 15,
    fontWeight: '700' as const,
    color: '#E4E6EB',
  },
  emptyInline: {
    fontSize: 13,
    color: '#B0B3B8',
  },
  contentTypeList: {
    gap: 10,
  },
  contentTypeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  contentTypeLabel: {
    width: 56,
    fontSize: 13,
    color: '#E4E6EB',
  },
  barTrack: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#3A3B3C',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 4,
    backgroundColor: '#E4E6EB',
  },
  contentTypePct: {
    width: 44,
    fontSize: 13,
    fontWeight: '600' as const,
    color: '#E4E6EB',
    textAlign: 'right',
  },
  donutRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  donutRing: {
    width: 96,
    height: 96,
    borderRadius: 48,
    borderWidth: 16,
    borderColor: '#4599FF',
  },
  donutLegend: {
    gap: 2,
  },
  donutPctPrimary: {
    fontSize: 16,
    fontWeight: '700' as const,
    color: '#E4E6EB',
  },
  donutPctSecondary: {
    fontSize: 16,
    fontWeight: '700' as const,
    color: '#E4E6EB',
    marginTop: 6,
  },
  donutLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  donutDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  donutLegendLabel: {
    fontSize: 12,
    color: '#B0B3B8',
  },
  recentPostsSection: {
    backgroundColor: '#242526',
    borderRadius: 10,
    padding: 16,
    gap: 10,
  },
  recentPostsTitle: {
    fontSize: 15,
    fontWeight: '700' as const,
    color: '#E4E6EB',
  },
  recentPostsRow: {
    gap: 12,
  },
  recentPostTile: {
    width: 100,
  },
  recentPostImage: {
    width: 100,
    height: 100,
    borderRadius: 8,
    marginBottom: 4,
  },
  recentPostImageFallback: {
    backgroundColor: '#3A3B3C',
    alignItems: 'center',
    justifyContent: 'center',
  },
  recentPostDate: {
    fontSize: 11,
    color: '#B0B3B8',
  },
  recentPostViews: {
    fontSize: 15,
    fontWeight: '700' as const,
    color: '#E4E6EB',
    marginTop: 2,
  },
  recentPostViewsLabel: {
    fontSize: 11,
    color: '#B0B3B8',
  },
});
